// Capability names are the vocabulary shared with the auth service: the
// session payload carries the list of capabilities the signed-in user holds,
// and every gate in the UI asks for one by name. The role defaults below are
// only a fallback for a session that predates the permissions work - the
// server list always wins, and the server enforces it regardless.
export const CAPABILITIES = {
  usersManage: "users.manage",
  permissionsManage: "permissions.manage",
  sessionsManage: "sessions.manage",
  auditView: "audit.view",
  loginAttemptsView: "login-attempts.view",
  servicesControl: "services.control",
  servicesLogs: "services.logs",
  alarmsManage: "alarms.manage",
  devicesView: "devices.view",
  devicesDelete: "devices.delete",
  devicesRemote: "devices.remote",
  devicesScript: "devices.script",
  devicesFiles: "devices.files",
  actionsSchedule: "actions.schedule",
  reportsView: "reports.view",
};

export const ALL_CAPABILITIES = Object.values(CAPABILITIES);

// Capabilities grouped for the permissions matrix UI. The group keys are
// translation keys under "permissions.groups".
export const CAPABILITY_GROUPS = [
  {
    key: "administration",
    capabilities: [
      CAPABILITIES.usersManage,
      CAPABILITIES.permissionsManage,
      CAPABILITIES.sessionsManage,
      CAPABILITIES.auditView,
      CAPABILITIES.loginAttemptsView,
    ],
  },
  {
    key: "infrastructure",
    capabilities: [
      CAPABILITIES.servicesControl,
      CAPABILITIES.servicesLogs,
      CAPABILITIES.alarmsManage,
    ],
  },
  {
    key: "devices",
    capabilities: [
      CAPABILITIES.devicesView,
      CAPABILITIES.devicesDelete,
      CAPABILITIES.devicesRemote,
      CAPABILITIES.devicesScript,
      CAPABILITIES.devicesFiles,
      CAPABILITIES.actionsSchedule,
      CAPABILITIES.reportsView,
    ],
  },
];

// The device platforms a role may see, matching the dashboard's own filters.
export const PLATFORMS = [
  "windows",
  "linux",
  "server",
  "mobile",
  "apple",
];

export const ROLES = ["admin", "reports", "guest"];

// Used only when the session has no capability list yet.
export const ROLE_DEFAULTS = {
  admin: {
    capabilities: ALL_CAPABILITIES,
    platforms: PLATFORMS,
  },
  reports: {
    capabilities: [CAPABILITIES.devicesView, CAPABILITIES.reportsView],
    platforms: PLATFORMS,
  },
  guest: {
    capabilities: [CAPABILITIES.devicesView],
    platforms: PLATFORMS,
  },
};

// A session issued before the service learned about roles carries neither a
// capability list nor a recognised role. Treating that as "no capabilities"
// would lock every operator out of the console the moment this ships, so the
// unknown case defers to the server instead - which rejects the request itself
// if the user really is not allowed. Once /auth/session returns capabilities
// or a known role, this branch stops being reached.
function capabilitiesOf(user) {
  if (!user) return [];
  if (Array.isArray(user.capabilities)) return user.capabilities;
  const fallback = ROLE_DEFAULTS[user.role];
  return fallback ? fallback.capabilities : ALL_CAPABILITIES;
}

export function can(user, capability) {
  if (!user) return false;
  return capabilitiesOf(user).includes(capability);
}

// True when the user holds at least one of the listed capabilities - used to
// decide whether a whole menu section is worth rendering.
export function canAny(user, capabilities) {
  return capabilities.some((capability) => can(user, capability));
}

export function isAdmin(user) {
  return !!user && user.role === "admin";
}

export function platformsOf(user) {
  if (!user) return [];
  if (Array.isArray(user.platforms)) return user.platforms;
  const fallback = ROLE_DEFAULTS[user.role];
  // Same reasoning as capabilitiesOf: an unknown session sees everything and
  // the server does the real filtering.
  return fallback ? fallback.platforms : PLATFORMS;
}

export function canSeePlatform(user, platform) {
  return platformsOf(user).includes(platform);
}
